import React from 'react'
import { RealEstateCategory, SegmentationType, TransactionType } from '../../../../../../@types/types'
import { Flow, FlowName, FlowScreenElement } from '../../../../../../@types/flow-types'
import { PROPERTY_OWNER_HOUSE_SELL_FLOW_PATH } from '../../../../../../services/routes/routes'
import { propertyOwnerHouseSellSchema } from './propertyOwnerHouseSellSchema'
import {
  SCREEN_ADDRESS,
  SCREEN_BASIC_INFO,
  SCREEN_CONSTRUCTION,
  SCREEN_CONTACT_INFO,
  SCREEN_DESCRIPTION,
  SCREEN_MULTIMEDIA,
  SCREEN_OTHER_FACILITIES,
  SCREEN_REALTOR_LEAD_ENGINE,
  SCREENS,
} from '../../../../../../services/domain/wizardScreens'
import ContactInfo from '../../../../../../components/pages/insertionPage/screens/contactInfo/ContactInfo'
import Address from '../../../../../../components/pages/insertionPage/screens/address/Address'
import Construction from '../../../../../../components/pages/insertionPage/screens/construction/Construction'
import Multimedia from '../../../../../../components/pages/insertionPage/screens/multimediaNew/Multimedia'

import { MultimediaUploaderType } from '../../../../../../@types/multimedia-types'
import Description from '../../../../../../components/pages/insertionPage/screens/description/Description'
import OtherFacilities from '../../../../../../components/pages/insertionPage/screens/otherFacilities/OtherFacilities'
import {
  bathroomField,
  constructionPhase,
  estateFeatures,
  interiorQuality,
  moveInDate,
  noBedrooms,
  parkingSpace,
  rentalIncome,
  totalFloorsField,
  usableAreaField,
} from '../../../../../../components/pages/insertionPage/screens/otherFacilities/wrapper/otherFacilitiesFieldsWrapper'
import { getDefaultClasses } from '../../../../../../utils/style-utils'
import { buildingTypeField } from '../../../../../../components/pages/insertionPage/screens/commonFields/buildingType/BuildingType'
import BasicInfo from '../../../../../../components/pages/insertionPage/screens/basicInfo/BasicInfo'
import {
  livingSpaceField,
  plotAreaField,
  priceField,
  priceMaximizer,
  purchasePriceOnRequestToggle,
  roomField,
} from '../../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoFieldsWrapper'
import { PriceFieldType } from '../../../../../../components/pages/insertionPage/screens/basicInfo/fields/priceField/PriceField'
import { priceToApiSell } from '../../../../../../api/realEstate/converters/toApi/estate/price/priceToApi'
import { RealtorLeadEngine } from '../../../../../../components/pages/insertionPage/screens/realtorLeadEngine/RealtorLeadEngine'
import { houseSellBuildingTypeOptions, houseSellEstateFeaturesList } from '../common'

const propertyOwnerHouseSellFlowScreens: FlowScreenElement[] = [
  {
    screen: SCREENS[SCREEN_ADDRESS],
    element: (props) => <Address {...props} isInternationalAddressOption={true} isPriceOverviewLinkDisplayed={true} />,
  },
  {
    screen: SCREENS[SCREEN_BASIC_INFO],
    element: (props) => (
      <BasicInfo
        {...props}
        screenTooltipKey='tooltip.screens.basicInfo.apartmentOrHouseSell'
        properties={[
          roomField(getDefaultClasses('left')),
          livingSpaceField('right'),
          priceField(PriceFieldType.BUY_PRICE, getDefaultClasses('left')),
          plotAreaField('right'),
          purchasePriceOnRequestToggle(),
          priceMaximizer(),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_OTHER_FACILITIES],
    element: (props) => {
      return (
        <OtherFacilities
          {...props}
          screenTooltipKey='tooltip.screens.otherFacilities.allSell'
          properties={[
            estateFeatures(houseSellEstateFeaturesList),
            interiorQuality(),
            buildingTypeField('house', houseSellBuildingTypeOptions),
            totalFloorsField(getDefaultClasses('right')),
            bathroomField(getDefaultClasses('left')),
            noBedrooms('right'),
            usableAreaField(undefined, getDefaultClasses('leftOnDesk')),
            constructionPhase(getDefaultClasses('rightOnDesk')),
            moveInDate(getDefaultClasses('left')),
            rentalIncome(),
            parkingSpace({
              showParkingSpaceType: true,
              hasGarageLabel: true,
              parkingSpaceRentTypeLabel: 'RentBuyingPrice',
            }),
          ]}
        />
      )
    },
  },
  {
    screen: SCREENS[SCREEN_DESCRIPTION],
    element: (props) => <Description {...props} hasFurnishingNote={true} hasLocationNote={true} hasOtherNote={true} />,
  },
  {
    screen: SCREENS[SCREEN_MULTIMEDIA],
    element: (props) => (
      <Multimedia
        {...props}
        doCloseMultimediaStatusMessage={props.doCloseMultimediaStatusMessage!}
        allowedTypesToDisplay={[
          MultimediaUploaderType.PICTURE,
          MultimediaUploaderType.FLOOR_PLAN,
          MultimediaUploaderType.DOCUMENT,
          MultimediaUploaderType.VIDEO,
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_CONSTRUCTION],
    element: (props) => <Construction {...props} hasLastModernizationField={true} />,
  },
  {
    screen: SCREENS[SCREEN_REALTOR_LEAD_ENGINE],
    element: (props) => <RealtorLeadEngine {...props} />,
  },
  {
    screen: SCREENS[SCREEN_CONTACT_INFO],
    element: (props) => <ContactInfo {...props} />,
  },
]

export const propertyOwnerHouseSellFlow: Flow = {
  flowInformation: {
    owner: 'wizard.navigation.header.owner',
    estateType: 'wizard.navigation.header.house',
    offerType: 'wizard.navigation.header.sells',
    path: PROPERTY_OWNER_HOUSE_SELL_FLOW_PATH,
    name: FlowName.PROPERTY_OWNER_HOUSE_SELL,
    validationSchema: propertyOwnerHouseSellSchema,
    priceToApiMapper: priceToApiSell,
    options: {
      hasTextSuggestions: true,
    },
    metaData: {
      category: RealEstateCategory.HOUSE,
      segmentation: SegmentationType.PROPERTY_OWNER,
      transaction: TransactionType.SELL,
    },
  },
  defaultStateValues: {
    hideStreetAndStreetNumber: false,
    rented: false,
    summerResidencePractical: false,
  },
  flowScreens: propertyOwnerHouseSellFlowScreens,
}
